/**
 * AutoDialerContext — global session state for the power-dialer that walks a
 * filtered lead list. From PR2 onward the dialer races up to N=1/3/5 lines
 * in parallel and uses Twilio's MachineDetection to auto-skip voicemails.
 *
 * The session has to survive route changes (an agent may navigate to a lead
 * detail page mid-call), so the state lives in context and is mounted once
 * at the app root next to VoiceCallProvider.
 *
 * Two flows are driven by AutoDialerController:
 *   1. Legacy sequential (parallelLines===1 && !amdEnabled) — uses the
 *      InBrowserCallButton <Dial> flow.
 *   2. Conference flow (parallelLines>1 || amdEnabled) — server-side REST
 *      calls into a per-batch Twilio Conference, AMD on each leg, race-fence
 *      via kill_batch_losers.
 */

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import type { SmsLeadWithCampaign } from "@/lib/sms-api";

export type AutoDialStatus =
    | "idle"
    | "dialing"
    | "connected"
    | "awaiting-disposition"
    | "cooldown"
    | "paused"
    | "finished";

export type ParallelLines = 1 | 3 | 5;

/** Outcome of an entire batch, surfaced in the cooldown label and used by
 *  the session bar to explain why nobody picked up. */
export type BatchOutcome = "connected" | "voicemail" | "no-answer" | "failed" | null;

/** Mirror of dialer_calls.status (plus a wider set covering Twilio's raw
 *  values pre-mapping). */
export type LegStatus =
    | "queued"
    | "ringing"
    | "in-progress"
    | "completed"
    | "failed"
    | "no-answer"
    | "busy"
    | "voicemail"
    | "cancelled";

export interface BatchLeg {
    leadId: string;
    /** dialer_calls.id — null briefly between session start and the edge
     *  function returning insert results. */
    callId: string | null;
    /** Twilio CallSid — null until Twilio accepts the REST POST. */
    twilioSid: string | null;
    leadName: string | null;
    leadPhone: string | null;
    status: LegStatus;
    amdResult: string | null;
}

export interface AutoDialerSession {
    businessId: string;
    fromNumberId: string;
    /** Already filtered to leads with a non-empty phone number. */
    queue: SmsLeadWithCampaign[];
    /** Index of the first lead in the CURRENT batch. Advances by
     *  currentBatch.length whenever a batch ends. */
    cursor: number;
    status: AutoDialStatus;
    /** Whether the winner leg bridged audio to the agent. Drives the
     *  "disposition required" branch on hangup. */
    currentConnected: boolean;
    /** Seconds remaining in the auto-advance cooldown. 0 when not counting. */
    cooldownRemaining: number;
    /** Snapshot of cooldown state so Pause / Resume can stop the clock without
     *  losing the position. */
    pausedFrom: AutoDialStatus | null;
    /** When true, the agent hears nothing during ringback — only the
     *  "connected" beep when the called party picks up. Forced ON whenever
     *  parallelLines > 1 (multiple simultaneous ringbacks are unusable). */
    muteRingback: boolean;
    /** Lines to race per batch. 1=sequential, 3/5=parallel. */
    parallelLines: ParallelLines;
    /** Auto-skip voicemails via Twilio AsyncAmdStatusCallback. */
    amdEnabled: boolean;
    /** The legs currently being raced. Length === 1 in sequential mode;
     *  length === parallelLines in parallel mode (truncated near end of queue). */
    currentBatch: BatchLeg[];
    /** UUID assigned by the edge function on start_outbound_call_session.
     *  Drives the realtime subscription that updates per-leg status. */
    currentBatchId: string | null;
    /** Conference name shared by the agent leg and all lead legs in this batch. */
    currentConferenceName: string | null;
    /** Index into currentBatch of the leg that won the race. null until
     *  the controller flags a winner. */
    winnerLegIndex: number | null;
    /** Outcome of the LAST completed batch — drives the cooldown label
     *  ("Voicemail · skipped", "No answer", etc.). */
    lastBatchOutcome: BatchOutcome;
}

const MUTE_RINGBACK_PREF_KEY = "autoDialer.muteRingback.v1";
const AMD_ENABLED_PREF_KEY = "autoDialer.amdEnabled.v1";
const SPOKEN_CUE_PREF_KEY = "autoDialer.spokenConnectCue.v1";
const PARALLEL_LINES_PREF_KEY = "autoDialer.parallelLines.v1";

function loadBoolPref(key: string, defaultValue: boolean): boolean {
    if (typeof window === "undefined") return defaultValue;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return defaultValue;
        return raw === "1" || raw === "true";
    } catch {
        return defaultValue;
    }
}

function saveBoolPref(key: string, v: boolean): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(key, v ? "1" : "0");
    } catch { /* noop */ }
}

function loadParallelLinesPref(): ParallelLines {
    if (typeof window === "undefined") return 1;
    try {
        const raw = window.localStorage.getItem(PARALLEL_LINES_PREF_KEY);
        if (raw === "3") return 3;
        if (raw === "5") return 5;
        return 1;
    } catch {
        return 1;
    }
}

function saveParallelLinesPref(v: ParallelLines): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(PARALLEL_LINES_PREF_KEY, String(v));
    } catch { /* noop */ }
}

const loadMuteRingbackPref = () => loadBoolPref(MUTE_RINGBACK_PREF_KEY, true);
const saveMuteRingbackPref = (v: boolean) => saveBoolPref(MUTE_RINGBACK_PREF_KEY, v);
const loadAmdEnabledPref = () => loadBoolPref(AMD_ENABLED_PREF_KEY, true);
const saveAmdEnabledPref = (v: boolean) => saveBoolPref(AMD_ENABLED_PREF_KEY, v);
const loadSpokenCuePref = () => loadBoolPref(SPOKEN_CUE_PREF_KEY, false);
const saveSpokenCuePref = (v: boolean) => saveBoolPref(SPOKEN_CUE_PREF_KEY, v);

interface AutoDialerContextValue {
    session: AutoDialerSession | null;
    muteRingbackPref: boolean;
    setMuteRingbackPref: (v: boolean) => void;
    amdEnabledPref: boolean;
    setAmdEnabledPref: (v: boolean) => void;
    spokenConnectCuePref: boolean;
    setSpokenConnectCuePref: (v: boolean) => void;
    parallelLinesPref: ParallelLines;
    setParallelLinesPref: (v: ParallelLines) => void;
    start: (opts: {
        businessId: string;
        fromNumberId: string;
        leads: SmsLeadWithCampaign[];
        muteRingback?: boolean;
        parallelLines?: ParallelLines;
        amdEnabled?: boolean;
    }) => void;
    pause: () => void;
    resume: () => void;
    stop: () => void;
    skip: () => void;
    setMuteRingback: (v: boolean) => void;
    /** Called by the controller after start_outbound_call_session resolves
     *  with the inserted legs. */
    markBatchStarted: (opts: {
        batchId: string;
        conferenceName: string;
        legs: BatchLeg[];
    }) => void;
    /** Called by the controller when a realtime update arrives for any leg
     *  in the current batch. */
    markLegStatus: (callId: string, patch: Partial<BatchLeg>) => void;
    /** Called by the controller when the winning leg has been bridged to
     *  the agent. */
    markWinnerSelected: (callId: string) => void;
    /** Called by the controller after the winning conference call ends OR
     *  every leg in the batch failed without a winner. */
    markBatchEnded: (outcome: BatchOutcome) => void;
    /** Legacy sequential path. Called by the controller when a call goes
     *  live in InBrowserCallButton's <Dial> flow. */
    markConnected: () => void;
    /** Legacy sequential path. Called by the controller when the SDK fires
     *  disconnect. */
    markEnded: () => void;
    markDispositioned: () => void;
    decrementCooldown: () => void;
}

const AutoDialerContext = createContext<AutoDialerContextValue | null>(null);

const COOLDOWN_SECONDS = 1;

export function leadHasPhone(lead: SmsLeadWithCampaign): boolean {
    const p = (lead.currentPhone || lead.phone1 || "").trim();
    return p.length > 0;
}

export function AutoDialerProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<AutoDialerSession | null>(null);
    const [muteRingbackPref, setMuteRingbackPrefState] = useState<boolean>(loadMuteRingbackPref);
    const [amdEnabledPref, setAmdEnabledPrefState] = useState<boolean>(loadAmdEnabledPref);
    const [spokenConnectCuePref, setSpokenConnectCuePrefState] = useState<boolean>(loadSpokenCuePref);
    const [parallelLinesPref, setParallelLinesPrefState] = useState<ParallelLines>(loadParallelLinesPref);

    // Track which cursors we've already started dialing for, so the controller's
    // effects don't double-fire start() if a render happens between transitions.
    const lastDialedCursorRef = useRef<number>(-1);

    const setMuteRingbackPref: AutoDialerContextValue["setMuteRingbackPref"] = useCallback((v) => {
        setMuteRingbackPrefState(v);
        saveMuteRingbackPref(v);
    }, []);

    const setAmdEnabledPref: AutoDialerContextValue["setAmdEnabledPref"] = useCallback((v) => {
        setAmdEnabledPrefState(v);
        saveAmdEnabledPref(v);
    }, []);

    const setSpokenConnectCuePref: AutoDialerContextValue["setSpokenConnectCuePref"] = useCallback((v) => {
        setSpokenConnectCuePrefState(v);
        saveSpokenCuePref(v);
    }, []);

    const setParallelLinesPref: AutoDialerContextValue["setParallelLinesPref"] = useCallback((v) => {
        setParallelLinesPrefState(v);
        saveParallelLinesPref(v);
    }, []);

    const start: AutoDialerContextValue["start"] = useCallback(
        ({ businessId, fromNumberId, leads, muteRingback, parallelLines, amdEnabled }) => {
            const queue = leads.filter(leadHasPhone);
            if (queue.length === 0) return;
            lastDialedCursorRef.current = -1;
            const initialMute = muteRingback ?? muteRingbackPref;
            const lines: ParallelLines = parallelLines ?? parallelLinesPref;
            const amd = amdEnabled ?? amdEnabledPref;
            // Force-mute ringback in parallel mode — agents can't usefully
            // hear N simultaneous ringbacks.
            const effectiveMute = lines > 1 ? true : initialMute;
            setSession({
                businessId,
                fromNumberId,
                queue,
                cursor: 0,
                status: "dialing",
                currentConnected: false,
                cooldownRemaining: 0,
                pausedFrom: null,
                muteRingback: effectiveMute,
                parallelLines: lines,
                amdEnabled: amd,
                currentBatch: [],
                currentBatchId: null,
                currentConferenceName: null,
                winnerLegIndex: null,
                lastBatchOutcome: null,
            });
        },
        [muteRingbackPref, parallelLinesPref, amdEnabledPref],
    );

    const setMuteRingback: AutoDialerContextValue["setMuteRingback"] = useCallback((v) => {
        setMuteRingbackPrefState(v);
        saveMuteRingbackPref(v);
        setSession(s => {
            if (!s) return s;
            // Parallel mode locks ringback to muted — no toggling mid-session.
            if (s.parallelLines > 1) return s;
            return { ...s, muteRingback: v };
        });
    }, []);

    const pause: AutoDialerContextValue["pause"] = useCallback(() => {
        setSession(s => {
            if (!s || s.status === "paused" || s.status === "idle" || s.status === "finished") return s;
            return { ...s, status: "paused", pausedFrom: s.status };
        });
    }, []);

    const resume: AutoDialerContextValue["resume"] = useCallback(() => {
        setSession(s => {
            if (!s || s.status !== "paused") return s;
            const next: AutoDialStatus = s.pausedFrom ?? "cooldown";
            return {
                ...s,
                status: next,
                pausedFrom: null,
                cooldownRemaining: next === "cooldown" ? COOLDOWN_SECONDS : s.cooldownRemaining,
            };
        });
    }, []);

    const stop: AutoDialerContextValue["stop"] = useCallback(() => {
        lastDialedCursorRef.current = -1;
        setSession(null);
    }, []);

    const markConnected: AutoDialerContextValue["markConnected"] = useCallback(() => {
        setSession(s => {
            if (!s) return s;
            if (s.status !== "dialing") return { ...s, currentConnected: true };
            return { ...s, status: "connected", currentConnected: true };
        });
    }, []);

    const markEnded: AutoDialerContextValue["markEnded"] = useCallback(() => {
        setSession(s => {
            if (!s) return s;
            if (s.status === "awaiting-disposition" || s.status === "cooldown") return s;
            if (s.currentConnected) {
                return { ...s, status: "awaiting-disposition" };
            }
            return {
                ...s,
                status: "cooldown",
                cooldownRemaining: COOLDOWN_SECONDS,
                lastBatchOutcome: s.lastBatchOutcome ?? "no-answer",
            };
        });
    }, []);

    const markBatchStarted: AutoDialerContextValue["markBatchStarted"] = useCallback(({ batchId, conferenceName, legs }) => {
        setSession(s => {
            if (!s) return s;
            return {
                ...s,
                currentBatch: legs,
                currentBatchId: batchId,
                currentConferenceName: conferenceName,
                winnerLegIndex: null,
                currentConnected: false,
            };
        });
    }, []);

    const markLegStatus: AutoDialerContextValue["markLegStatus"] = useCallback((callId, patch) => {
        setSession(s => {
            if (!s) return s;
            const idx = s.currentBatch.findIndex(l => l.callId === callId);
            if (idx === -1) return s;
            const updated = [...s.currentBatch];
            updated[idx] = { ...updated[idx], ...patch };
            return { ...s, currentBatch: updated };
        });
    }, []);

    const markWinnerSelected: AutoDialerContextValue["markWinnerSelected"] = useCallback((callId) => {
        setSession(s => {
            if (!s) return s;
            const idx = s.currentBatch.findIndex(l => l.callId === callId);
            if (idx === -1) return s;
            return {
                ...s,
                status: "connected",
                currentConnected: true,
                winnerLegIndex: idx,
            };
        });
    }, []);

    const advanceBatch = useCallback((outcome: BatchOutcome) => {
        setSession(s => {
            if (!s) return s;
            const batchSize = Math.max(s.currentBatch.length || s.parallelLines, 1);
            const nextCursor = s.cursor + batchSize;
            if (nextCursor >= s.queue.length) {
                return {
                    ...s,
                    status: "finished",
                    cursor: nextCursor,
                    cooldownRemaining: 0,
                    lastBatchOutcome: outcome,
                    currentBatch: [],
                    currentBatchId: null,
                    currentConferenceName: null,
                    winnerLegIndex: null,
                };
            }
            return {
                ...s,
                cursor: nextCursor,
                status: "dialing",
                currentConnected: false,
                cooldownRemaining: 0,
                pausedFrom: null,
                lastBatchOutcome: outcome,
                currentBatch: [],
                currentBatchId: null,
                currentConferenceName: null,
                winnerLegIndex: null,
            };
        });
    }, []);

    const markBatchEnded: AutoDialerContextValue["markBatchEnded"] = useCallback((outcome) => {
        setSession(s => {
            if (!s) return s;
            // A winner that bridged audio routes through awaiting-disposition;
            // dead batches go straight to cooldown.
            if (outcome === "connected") {
                if (s.status === "awaiting-disposition") return s;
                return { ...s, status: "awaiting-disposition", lastBatchOutcome: outcome };
            }
            // Voicemail / no-answer / failed → cooldown, no disposition required.
            return {
                ...s,
                status: "cooldown",
                cooldownRemaining: COOLDOWN_SECONDS,
                lastBatchOutcome: outcome,
            };
        });
    }, []);

    const markDispositioned: AutoDialerContextValue["markDispositioned"] = useCallback(() => {
        setSession(s => {
            if (!s || s.status !== "awaiting-disposition") return s;
            return { ...s, status: "cooldown", cooldownRemaining: COOLDOWN_SECONDS };
        });
    }, []);

    const skip: AutoDialerContextValue["skip"] = useCallback(() => {
        advanceBatch(null);
    }, [advanceBatch]);

    const decrementCooldown: AutoDialerContextValue["decrementCooldown"] = useCallback(() => {
        setSession(s => {
            if (!s || s.status !== "cooldown") return s;
            const next = s.cooldownRemaining - 1;
            if (next <= 0) {
                const batchSize = Math.max(s.currentBatch.length || s.parallelLines, 1);
                const nextCursor = s.cursor + batchSize;
                if (nextCursor >= s.queue.length) {
                    return {
                        ...s,
                        status: "finished",
                        cursor: nextCursor,
                        cooldownRemaining: 0,
                        currentBatch: [],
                        currentBatchId: null,
                        currentConferenceName: null,
                        winnerLegIndex: null,
                    };
                }
                return {
                    ...s,
                    cursor: nextCursor,
                    status: "dialing",
                    currentConnected: false,
                    cooldownRemaining: 0,
                    currentBatch: [],
                    currentBatchId: null,
                    currentConferenceName: null,
                    winnerLegIndex: null,
                };
            }
            return { ...s, cooldownRemaining: next };
        });
    }, []);

    const value = useMemo<AutoDialerContextValue>(
        () => ({
            session,
            muteRingbackPref,
            setMuteRingbackPref,
            amdEnabledPref,
            setAmdEnabledPref,
            spokenConnectCuePref,
            setSpokenConnectCuePref,
            parallelLinesPref,
            setParallelLinesPref,
            start,
            pause,
            resume,
            stop,
            skip,
            setMuteRingback,
            markBatchStarted,
            markLegStatus,
            markWinnerSelected,
            markBatchEnded,
            markConnected,
            markEnded,
            markDispositioned,
            decrementCooldown,
        }),
        [
            session, muteRingbackPref, setMuteRingbackPref,
            amdEnabledPref, setAmdEnabledPref,
            spokenConnectCuePref, setSpokenConnectCuePref,
            parallelLinesPref, setParallelLinesPref,
            start, pause, resume, stop, skip, setMuteRingback,
            markBatchStarted, markLegStatus, markWinnerSelected, markBatchEnded,
            markConnected, markEnded, markDispositioned, decrementCooldown,
        ],
    );

    return <AutoDialerContext.Provider value={value}>{children}</AutoDialerContext.Provider>;
}

export function useAutoDialer(): AutoDialerContextValue {
    const ctx = useContext(AutoDialerContext);
    if (!ctx) throw new Error("useAutoDialer must be used inside AutoDialerProvider");
    return ctx;
}
